import GalaxyWordmark from "@galaxy-io/dls/brand/GalaxyWordmark";
import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Divider from "@galaxy-io/dls/layout/Divider";
import Flex, { AlignItems, FlexDirection } from "@galaxy-io/dls/layout/Flex";
import Breadcrumbs, { BreadcrumbsSize } from "@galaxy-io/dls/navigation/Breadcrumbs";
import SidebarNav, { NavGroup, NavItem } from "@galaxy-io/dls/navigation/SidebarNav";
import Topbar from "@galaxy-io/dls/navigation/Topbar";
import Text, { TextVariant } from "@galaxy-io/dls/text/Text";
import { FontFamily, Orientation } from "@galaxy-io/dls/theme/enums";
import type { Workspace } from "@gtm/contracts";
import {
  BookOpenTextIcon,
  BroadcastIcon,
  BuildingsIcon,
  ClockCounterClockwiseIcon,
  CodeIcon,
  FlowArrowIcon,
  ListIcon,
  MagnifyingGlassIcon,
  type Icon as PhosphorIcon,
  ProhibitIcon,
  QuestionIcon,
  SidebarSimpleIcon,
  SignOutIcon,
  SlidersHorizontalIcon,
  SquaresFourIcon,
  StackIcon,
  TagIcon,
  UserCircleIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { Link, usePathname, useRouter, useSearchParams } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { CommandMenu } from "@/components/workspace/command-menu";
import { SelectField } from "@/components/workspace/select-field";
import {
  Body,
  Content,
  DesktopHeader,
  Frame,
  GlobalHeader,
  Island,
  Navigation,
  Surface,
} from "@/components/workspace/styles";
import { RecordInspectors } from "@/features/activity/record-inspectors";
import { CompanyInspector } from "@/features/companies/company-inspector";
import { ExclusionEditor } from "@/features/exclusions/rule-form";
import { EntityEditors } from "@/features/people/entity-editor";
import { PeopleOverlay } from "@/features/people/person-inspector";
import { logout } from "@/server/auth-actions";

type NavigationItem = {
  href: string;
  label: string;
  icon: PhosphorIcon;
  children?: NavigationItem[];
};

export function Shell({
  children,
  workspaceId,
  workspaces,
  user,
}: {
  children: React.ReactNode;
  workspaceId: string;
  workspaces: Workspace[];
  user: { id: string; name: string };
}) {
  const pathname = usePathname();
  const router = useRouter();
  const params = useSearchParams();
  const [expanded, setExpanded] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const isAdmin = workspaces.find((w) => w.id === workspaceId)?.role === "admin";
  const section =
    pathname === "/workflows"
      ? "workflows"
      : ["/exclusions", "/knowledge", "/qa", "/product-requests", "/tags"].includes(pathname)
        ? "configuration"
        : pathname.startsWith("/settings")
          ? "account"
          : null;
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() =>
    section ? { [section]: true } : {},
  );
  useEffect(() => {
    if (section) setOpenSections((current) => ({ ...current, [section]: true }));
  }, [section]);
  const items: NavigationItem[] = [
    { href: "/", label: "Signals", icon: SquaresFourIcon },
    { href: "/people", label: "People", icon: UsersIcon },
    { href: "/companies", label: "Companies", icon: BuildingsIcon },
    { href: "/integrations", label: "Integrations", icon: StackIcon },
    { href: "/observability", label: "Observability", icon: BroadcastIcon },
    { href: "/query", label: "Console", icon: CodeIcon },
    {
      href: "workflows",
      label: "Workflows",
      icon: FlowArrowIcon,
      children: [
        { href: "/workflows", label: "Library", icon: StackIcon },
        { href: "/workflows?view=history", label: "Runs", icon: ClockCounterClockwiseIcon },
        { href: "/workflows?view=schedules", label: "Schedules", icon: ClockCounterClockwiseIcon },
        { href: "/workflows?view=history&kind=delivery", label: "Deliveries", icon: FlowArrowIcon },
      ],
    },
    {
      href: "configuration",
      label: "Configuration",
      icon: SlidersHorizontalIcon,
      children: [
        { href: "/dream-list", label: "Dream List", icon: BuildingsIcon },
        { href: "/exclusions", label: "Exclusions", icon: ProhibitIcon },
        { href: "/knowledge", label: "Knowledge Center", icon: BookOpenTextIcon },
        { href: "/qa", label: "Q&A", icon: QuestionIcon },
        { href: "/product-requests", label: "Product Requests", icon: StackIcon },
        { href: "/tags", label: "Tags", icon: TagIcon },
      ],
    },
  ];
  function active(href: string) {
    const [path, search] = href.split("?");
    if (search)
      return (
        pathname === path &&
        (path !== "/workflows" ||
          (new URLSearchParams(search).get("view") === (params.get("view") || null) &&
            new URLSearchParams(search).get("kind") === params.get("kind"))) &&
        [...new URLSearchParams(search)].every(([key, value]) => params.get(key) === value)
      );
    return pathname === path && (path !== "/workflows" || !params.get("view"));
  }
  return (
    <Frame>
      <EntityEditors />
      <PeopleOverlay />
      <CompanyInspector />
      <RecordInspectors />
      <ExclusionEditor />
      <GlobalHeader>
        <Topbar
          actions={
            <Button
              icon={MagnifyingGlassIcon}
              ariaLabel="Search workspace"
              tooltip="Search workspace"
              variant={ButtonVariant.TERTIARY}
              size={ButtonSize.SMALL}
              onClick={() => setCommandOpen(true)}
            />
          }
          leading={
            <Button
              ariaLabel="Toggle navigation"
              tooltip="Toggle navigation"
              aria-expanded={expanded}
              icon={ListIcon}
              variant={ButtonVariant.TERTIARY}
              size={ButtonSize.SMALL}
              onClick={() => {
                setCollapsed(false);
                setExpanded(!expanded);
              }}
            />
          }
        >
          <Flex alignItems={AlignItems.CENTER} gap={8}>
            <GalaxyWordmark size={20} />
            <Text family={FontFamily.MONO}>GTM</Text>
          </Flex>
        </Topbar>
      </GlobalHeader>
      <Body>
        <Navigation $expanded={expanded} $collapsed={collapsed}>
          <SidebarNav
            ariaLabel="Section navigation"
            isCollapsed={collapsed}
            header={
              <Flex alignItems={AlignItems.CENTER} gap={8} fillWidth>
                {!collapsed && (
                  <>
                    <Link href="/" aria-label="Galaxy GTM home">
                      <GalaxyWordmark size={20} />
                    </Link>
                    <Text variant={TextVariant.TERTIARY} family={FontFamily.MONO}>
                      GTM
                    </Text>
                  </>
                )}
                <Flex grow={1} />
                <Button
                  ariaLabel={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                  tooltip={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                  aria-expanded={!collapsed}
                  icon={SidebarSimpleIcon}
                  size={ButtonSize.SMALL}
                  variant={ButtonVariant.TERTIARY}
                  onClick={() => setCollapsed(!collapsed)}
                />
              </Flex>
            }
            footer={
              <Flex direction={FlexDirection.COLUMN} gap={8}>
                {workspaces.length > 1 && !collapsed && (
                  <SelectField
                    compact
                    width="100%"
                    label="Workspace"
                    value={workspaceId}
                    options={workspaces.map((w) => ({ value: w.id, label: w.name }))}
                    onChange={(id) => router.push(`/w/${id}`)}
                  />
                )}
                <NavItem
                  as={Link}
                  href="/settings/preferences"
                  label={collapsed ? `Settings for ${user.name}` : user.name}
                  tooltip={`Settings for ${user.name}`}
                  icon={UserCircleIcon}
                  isActive={section === "account"}
                  onClick={() => setExpanded(false)}
                />
                <NavItem label="Sign out" icon={SignOutIcon} onClick={() => void logout()} />
              </Flex>
            }
          >
            {items.map((item) =>
              item.children ? (
                <NavGroup
                  key={item.href}
                  label={item.label}
                  isCollapsible
                  isOpen={!!openSections[item.href]}
                  onOpenChange={(isOpen) =>
                    setOpenSections((current) => ({ ...current, [item.href]: isOpen }))
                  }
                >
                  {item.children.map((child) => (
                    <NavItem
                      key={child.href}
                      as={Link}
                      href={child.href}
                      label={child.label}
                      icon={child.icon}
                      isActive={active(child.href)}
                      onClick={() => setExpanded(false)}
                    />
                  ))}
                </NavGroup>
              ) : (
                <NavItem
                  key={item.href}
                  as={Link}
                  href={item.href}
                  label={item.label}
                  icon={item.icon}
                  isActive={active(item.href)}
                  onClick={() => setExpanded(false)}
                />
              ),
            )}
          </SidebarNav>
        </Navigation>
        <Divider orientation={Orientation.VERTICAL} />
        <Island $collapsed={collapsed}>
          <Surface>
            <DesktopHeader>
              <Topbar
                actions={
                  <Button
                    label="Search workspace"
                    icon={MagnifyingGlassIcon}
                    size={ButtonSize.SMALL}
                    variant={ButtonVariant.TERTIARY}
                    hotKeys={["mod", "k"]}
                    onClick={() => setCommandOpen(true)}
                  />
                }
              >
                <Breadcrumbs
                  size={BreadcrumbsSize.SMALL}
                  items={[
                    {
                      label:
                        workspaces.find((workspace) => workspace.id === workspaceId)?.name ||
                        "Workspace",
                    },
                    {
                      label: pathname.startsWith("/settings")
                        ? "Settings"
                        : items
                            .flatMap((item) => item.children ?? [item])
                            .find((item) => active(item.href))?.label || "GTM",
                    },
                  ]}
                />
              </Topbar>
            </DesktopHeader>
            <Content
              $fixed={[
                "/query",
                "/people",
                "/companies",
                "/",
                "/observability",
                "/exclusions",
                "/knowledge",
                "/qa",
                "/product-requests",
                "/tags",
                "/workflows",
              ].includes(pathname)}
              id="workspace-content"
            >
              {children}
            </Content>
          </Surface>
        </Island>
      </Body>
      <CommandMenu open={commandOpen} onOpenChange={setCommandOpen} isAdmin={isAdmin} />
    </Frame>
  );
}
